import { CreditCard, Home, Mail, RefreshCw, XCircle } from "lucide-react";
import { Button } from "antd";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../misc/Card";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, AlertDescription } from "../misc/Alert";
import { useEffect, useState } from "react";
import axiosInstance from "../../../utility/axiosInstance";

type BusinessAcc = {
  id: string;
  name: string;
};

export function SubscriptionFailedPage() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const tier = searchParams.get("tier");
  const [biz, setBusiness] = useState<BusinessAcc | null>(null);

  const getBusinessData = async () => {
    try {
      const organizationId = id;

      if (!organizationId) {
        console.warn("Organization ID not found");
        return;
      }

      const response = await axiosInstance.get(
        `/organizations/${organizationId}/slight`,
      );
      if (response.data?.status === "success" && response.data?.data) {
        setBusiness(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching business data:", err);
    }
  };

  useEffect(() => {
    getBusinessData();
  }, []);
  const navigate = useNavigate();
  const handleRetryPayment = () => {
    navigate(
      `/payment-selection?Id=${id}${
        tier ? `&tier=${tier}` : ""
      }&provider=paypal`,
    );
    console.log("Retrying payment...");
    // In a real app, this would redirect to PayPal or retry the subscription
  };

  const handleContactSupport = () => {
    navigate(`mailto:solutions@25thmail.com`);
    console.log("Opening support...");
    // In a real app, this would open support chat or email
  };

  const handleReturnHome = () => {
    navigate(`/payment-selection?Id=${id}${tier ? `&tier=${tier}` : ""}`);
    // In a real app, this would navigate to home page
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Main Error Icon */}
        <div className="flex justify-center mb-3">
          <div className="relative">
            <div className="absolute inset-0 bg-[red] rounded-full blur-2xl opacity-20 animate-pulse"></div>
            <div className="relative bg-white rounded-full p-3 shadow-lg">
              <XCircle className="w-16 h-16 text-[red]" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        {/* Main Card */}
        <Card className="border-0 shadow-2xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-[red] mb-2">
              Subscription Payment Failed
            </CardTitle>
            <CardDescription className="text-lg">
              We couldn't process your PayPal subscription payment
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Error Details */}
            <Alert className="bg-[red]/5 border border-[red]/40 rounded-8">
              <AlertDescription className="text-[red] p-3">
                <div className="space-y-2">
                  <p>
                    Your subscription payment was declined. This could be due
                    to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Insufficient funds in your PayPal account</li>
                    <li>Payment method needs to be updated</li>
                    <li>Temporary issue with PayPal services</li>
                    <li>Payment authorization was cancelled</li>
                  </ul>
                </div>
              </AlertDescription>
            </Alert>

            {/* Transaction Details */}
            <div className="bg-[gray]/5 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Transaction ID:</span>
                <span className="font-mono text-sm">
                  PP-{Math.random().toString(36).substr(2, 9).toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Date:</span>
                <span>
                  {new Date().toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Payment Method:</span>
                <div className="flex items-center gap-2">
                  <span>PayPal</span>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.794.68l-.04.22-.63 3.993-.029.148a.804.804 0 0 1-.794.68H8.25c-.42 0-.713-.37-.622-.78l2.145-13.587a.805.805 0 0 1 .794-.68h4.99c1.758 0 3.082.365 3.934 1.087.378.32.68.709.88 1.165.07.162.13.332.18.51.054.187.095.382.122.584.028.203.043.415.043.637 0 .101-.003.203-.01.305-.006.1-.015.2-.027.299z"
                      fill="#139AD6"
                    />
                    <path
                      d="M19.997 8.173c-.027-.097-.057-.193-.09-.287a5.042 5.042 0 0 0-.18-.51 4.255 4.255 0 0 0-.88-1.165c-.852-.722-2.176-1.087-3.934-1.087h-4.99a.805.805 0 0 0-.794.68L6.984 19.391c-.091.41.202.78.622.78h2.815l.63-3.993.04-.22a.805.805 0 0 1 .794-.68h.5c3.238 0 5.774-1.314 6.514-5.12.256-1.313.192-2.447-.3-3.327a2.76 2.76 0 0 0-.602-.658z"
                      fill="#263B80"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid gap-3 pt-2">
              <Button
                onClick={handleRetryPayment}
                className="w-full bg-primary hover:bg-secondary text-white h-12"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Retry Payment with PayPal
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <Button onClick={handleContactSupport} className="h-12">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>

                <Button onClick={handleReturnHome} className="h-12">
                  <Home className="w-4 h-4 mr-2" />
                  Return to Payment Selection
                </Button>
              </div>
            </div>

            {/* Help Section */}
            <div className="border-t pt-6 mt-6">
              <div className="flex items-start gap-3 text-sm">
                <CreditCard className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-gray-700">
                    <span>Need to update your payment method?</span>
                  </p>
                  <p className="text-gray-600">
                    Log in to your PayPal account to update your payment
                    information or ensure you have sufficient funds available.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Note */}
            <div className="text-center text-sm text-gray-500 pt-2 pb-3">
              If you continue to experience issues, please contact our support
              team for assistance.
            </div>
          </CardContent>
        </Card>

        {/* Security Badge */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Secure payment processing by PayPal
          </p>
        </div>
      </div>
    </div>
  );
}
